export const QUOTE_STATUSES = ["draft", "approved"] as const;

export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const DEFAULT_QUOTE_STATUS: QuoteStatus = "draft";

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: "Draft",
  approved: "Approved",
};

export function isQuoteStatus(value: unknown): value is QuoteStatus {
  return (
    typeof value === "string" &&
    (QUOTE_STATUSES as readonly string[]).includes(value)
  );
}

export function quoteStatusLabel(status: string | null | undefined): string {
  if (!status) return QUOTE_STATUS_LABELS[DEFAULT_QUOTE_STATUS];
  const lower = status.toLowerCase();
  if (isQuoteStatus(lower)) return QUOTE_STATUS_LABELS[lower];
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Coerce any raw quote status into the canonical workflow status. The quote
 * workflow only knows `draft` / `approved`; payment state is derived from
 * receipts and is NOT a quote status. Anything that is not the literal
 * `draft` (including legacy `paid` / `partial` / `pending` rows written by
 * older receipt code, or `approved`) is treated as `approved`, so a quote
 * that has reached the payment stage is never mislabelled as Draft.
 */
export function coerceQuoteStatus(
  status: string | null | undefined,
): QuoteStatus {
  const lower = (status ?? "").trim().toLowerCase();
  if (!lower) return DEFAULT_QUOTE_STATUS;
  return lower === "draft" ? "draft" : "approved";
}
